import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, MapPin, Plus, Trash2, History, User, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { useCustomerAuth } from "@/lib/customerAuth";
import { api, euro } from "@/lib/api";

const STATUS_LABEL = {
  pending_payment: "En attente", confirmed: "Confirmée", completed: "Terminée", cancelled: "Annulée",
};

export default function MemberDashboard() {
  const { customer, logout, refresh } = useCustomerAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [newAddr, setNewAddr] = useState({ label: "", address: "", lat: null, lng: null });

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/customer/addresses"), api.get("/customer/bookings")]);
      setAddresses(a.data);
      setBookings(b.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const addAddress = async (e) => {
    e.preventDefault();
    if (!newAddr.label || !newAddr.address) { toast.error("Indiquez un nom et une adresse."); return; }
    try {
      const { data } = await api.post("/customer/addresses", newAddr);
      setAddresses((a) => [...a, data]);
      setNewAddr({ label: "", address: "", lat: null, lng: null });
      toast.success("Adresse enregistrée.");
      refresh();
    } catch { toast.error("Échec de l'enregistrement."); }
  };

  const removeAddress = async (id) => {
    try {
      await api.delete(`/customer/addresses/${id}`);
      setAddresses((a) => a.filter((x) => x.id !== id));
      refresh();
    } catch { toast.error("Échec de la suppression."); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="member-dashboard" className="pt-32 pb-24">
      <section className="mx-auto max-w-6xl px-5 lg:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {customer?.picture
              ? <img src={customer.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-gold/40" />
              : <span className="grid h-14 w-14 place-items-center rounded-full bg-gold/15 text-gold"><User size={22} /></span>}
            <div>
              <p className="text-gold text-xs tracking-[0.35em] uppercase">Espace Membre</p>
              <h1 className="font-serif text-3xl text-white">Bonjour {customer?.first_name || customer?.name || "cher client"}</h1>
              <p className="text-white/45 text-sm">{customer?.email}</p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="member-logout" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 hover:border-gold/50 transition-colors">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          {/* Saved addresses */}
          <div className="rounded-2xl border border-white/10 bg-panel p-8">
            <h2 className="flex items-center gap-2 font-serif text-2xl text-white"><Star size={18} className="text-gold" /> Adresses enregistrées</h2>
            <div className="mt-6 space-y-3" data-testid="saved-addresses">
              {addresses.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-white text-sm">{a.label}</p>
                    <p className="text-white/45 text-xs truncate flex items-center gap-1"><MapPin size={11} className="text-gold" /> {a.address}</p>
                  </div>
                  <button onClick={() => removeAddress(a.id)} data-testid={`delete-address-${a.id}`} className="text-white/40 hover:text-red-400 transition-colors shrink-0"><Trash2 size={16} /></button>
                </div>
              ))}
              {addresses.length === 0 && <p className="text-white/40 text-sm">Aucune adresse enregistrée pour l'instant.</p>}
            </div>

            <form onSubmit={addAddress} className="mt-6 space-y-3 border-t border-white/10 pt-6">
              <Input data-testid="address-label" value={newAddr.label} onChange={(e) => setNewAddr((n) => ({ ...n, label: e.target.value }))} placeholder="Nom (ex : Domicile, Bureau)" className="bg-ink border-white/15 focus-visible:ring-gold" />
              <AddressAutocomplete testid="address-search" value={newAddr.address} onChange={(v) => setNewAddr((n) => ({ ...n, address: v, lat: null, lng: null }))} onSelect={(a) => setNewAddr((n) => ({ ...n, address: a.label, lat: a.lat, lng: a.lng }))} placeholder="Rechercher une adresse…" />
              <button type="submit" data-testid="add-address-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-black hover:bg-goldsoft transition-colors"><Plus size={15} /> Ajouter</button>
            </form>
          </div>

          {/* Ride history */}
          <div className="rounded-2xl border border-white/10 bg-panel p-8">
            <h2 className="flex items-center gap-2 font-serif text-2xl text-white"><History size={18} className="text-gold" /> Historique des courses</h2>
            <div className="mt-6 space-y-3" data-testid="ride-history">
              {bookings.map((b) => (
                <div key={b.id} className="rounded-xl border border-white/10 px-4 py-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gold font-mono text-xs">{b.reference}</span>
                    <span className="text-xs text-white/50">{b.date} {b.time}</span>
                  </div>
                  <p className="mt-2 text-sm text-white/80 truncate">
                    {b.pickup}{b.trip_type !== "hourly" ? ` → ${b.dropoff}` : ""}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-white/45">{b.vehicle_name} · {STATUS_LABEL[b.status] || b.status}</span>
                    <span className="text-white/80">{euro(b.amount)}</span>
                  </div>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-white/40 text-sm">Aucune course pour l'instant. Vos réservations apparaîtront ici.</p>}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
